/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {CellPosition, Column, EditableCallbackParams, GridApi, IRowNode, RowPinnedType} from 'ag-grid-community';

const ROW_REDRAW_RETRY_CAP = 120;

/**
 * Refresh only the cell that finished editing, then redraw the row for row-level class rules once no cell is in edit mode.
 * A full synchronous redrawRows during cellEditingStopped breaks same-row Tab: the next cell's editor appears then is destroyed.
 */
export function refreshCellAndDeferredRowRedraw(api: GridApi, node: IRowNode, column: Column | null | undefined): void {
    if (!column || (typeof api.isDestroyed === 'function' && api.isDestroyed())) {
        return;
    }
    const columnsToRefresh: Column[] = [column];
    const actionsColumn = api.getColumn('Actions');
    if (actionsColumn && actionsColumn !== column) {
        columnsToRefresh.push(actionsColumn);
    }
    api.refreshCells({rowNodes: [node], columns: columnsToRefresh, force: true});

    const tryRedrawRow = (attempt: number): void => {
        if (typeof api.isDestroyed === 'function' && api.isDestroyed()) {
            return;
        }
        if (attempt > ROW_REDRAW_RETRY_CAP) {
            return;
        }
        if (api.getEditingCells().length > 0) {
            setTimeout(() => tryRedrawRow(attempt + 1), 0);
            return;
        }
        api.redrawRows({rowNodes: [node]});
    };
    setTimeout(() => tryRedrawRow(0), 0);
}

/** PrimeNG overlays are often portaled to body (outside the ag-grid host). */
const PRIME_OVERLAY_SELECTORS = [
    '.p-dropdown-panel',
    '.p-multiselect-panel',
    '.p-select-overlay',
    '.p-overlaypanel',
    '.p-connected-overlay',
    '.p-connected-overlay-enter-done'
].join(', ');

export function isEventTargetInPrimeOverlay(target: EventTarget | null): boolean {
    return !!(target as HTMLElement | null)?.closest?.(PRIME_OVERLAY_SELECTORS);
}

/** p-dialog (e.g. Hole Criteria cell editor) is often inside or outside the grid; Tab must not commit the grid edit. */
export function isEventTargetInPrimeDialog(target: EventTarget | null): boolean {
    return !!(target as HTMLElement | null)?.closest?.('.p-dialog');
}

function computeNextDisplayedTabStop(api: GridApi, from: CellPosition, backwards: boolean): CellPosition | null {
    const all = api.getAllDisplayedColumns();
    if (!all.length) {
        return null;
    }

    let pos: CellPosition = {
        rowIndex: from.rowIndex!,
        column: from.column,
        rowPinned: from.rowPinned ?? null
    };

    const rowCount = api.getDisplayedRowCount();
    const maxSteps = Math.max(1, (all.length + 2) * (rowCount + 2));

    for (let step = 0; step < maxSteps; step++) {
        const col = pos.column;
        const rowIdx = pos.rowIndex!;
        const rowPin = pos.rowPinned ?? null;

        let nCol: Column | null;
        let nRow = rowIdx;
        const nPin: RowPinnedType | null | undefined = rowPin;

        if (!backwards) {
            nCol = api.getDisplayedColAfter(col);
            if (!nCol) {
                nCol = all[0];
                if (nRow >= rowCount - 1) {
                    return null;
                }
                nRow++;
            }
        } else {
            nCol = api.getDisplayedColBefore(col);
            if (!nCol) {
                nCol = all[all.length - 1];
                if (nRow <= 0) {
                    return null;
                }
                nRow--;
            }
        }

        if (!nCol) {
            return null;
        }

        pos = {rowIndex: nRow, column: nCol, rowPinned: nPin};

        const rowNode = api.getDisplayedRowAtIndex(nRow);
        if (!rowNode) {
            return pos;
        }
        if (!nCol.isSuppressNavigable(rowNode)) {
            return pos;
        }
    }

    return null;
}

function isColEditableForRow(api: GridApi, column: Column, rowNode: IRowNode | undefined): boolean {
    if (!rowNode) {
        return false;
    }
    const colDef = column.getColDef();
    const e = colDef.editable;
    if (typeof e === 'function') {
        const params = {data: rowNode.data, node: rowNode, column, colDef, api} as unknown as EditableCallbackParams;
        return !!e(params);
    }
    return !!e;
}

/** Commit edit, move one displayed column (wrap row), and open the next cell for editing like native Tab. */
export function stopEditingAndMoveTabHorizontally(api: GridApi, backwards: boolean, keyboardEvent?: KeyboardEvent): void {
    const cells = api.getEditingCells();
    if (cells.length === 0) {
        return;
    }
    const from = cells[0];

    // Prefer grid-native Tab: it runs stopEditing + startEditing on the next cell (see NavigationService.moveToNextEditingCell).
    // Our previous setFocusedCell-only path left the next cell selected but not editing, so typing did nothing.
    if (!api.getFocusedCell()) {
        api.setFocusedCell(from.rowIndex!, from.column, from.rowPinned ?? undefined);
    }
    const moved = backwards ? api.tabToPreviousCell(keyboardEvent) : api.tabToNextCell(keyboardEvent);
    if (moved) {
        return;
    }

    if (api.getEditingCells().length > 0) {
        api.stopEditing(false);
    }

    const next = computeNextDisplayedTabStop(api, from, backwards);
    if (!next) {
        return;
    }

    api.ensureIndexVisible(next.rowIndex!);
    api.ensureColumnVisible(next.column);
    api.setFocusedCell(next.rowIndex!, next.column, next.rowPinned ?? undefined);

    const rowNode = api.getDisplayedRowAtIndex(next.rowIndex!);
    if (!isColEditableForRow(api, next.column, rowNode ?? undefined)) {
        return;
    }

    const initialKey =
        keyboardEvent?.key &&
        keyboardEvent.key.length === 1 &&
        !keyboardEvent.ctrlKey &&
        !keyboardEvent.metaKey &&
        !keyboardEvent.altKey
            ? keyboardEvent.key
            : undefined;

    api.startEditingCell({
        rowIndex: next.rowIndex!,
        colKey: next.column,
        rowPinned: next.rowPinned ?? undefined,
        key: initialKey
    });
}
